// src/components/comments/CommentSidebar.jsx
import React, { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CommentList from './CommentList';
import CommentComposer from './CommentComposer';
import '../../styles/CommentSidebar.css';
import { commentApi } from '../../utils/readerApi';

/**
 * Controlled panel:
 *  - Parent (BookReader) decides when it's open via the `open` prop
 *  - Parent closes it via the `onClose` callback (the "X" calls onClose)
 *  - Uses backend API (no mock data)
 *
 * Props expected from parent:
 *  - bookId: string (required)
 *  - itemType: "book" | "draft" (default "book")
 *  - currentPage: number (used when "Show Page Only" is active)
 */

export default function CommentSidebar({
  open,
  onClose,
  currentPage = 1,
  bookId,
  itemType = 'book',
  defaultShowAll = false,
  hideFilter = false,
  onBeginQuotePicking,
  onPickQuote,
  onEndQuotePicking,
  onRegisterQuoteAppend,
  onComposeOpen,
  onComposeClose,
  showComposer,
  onToggleComposer,
  seedQuote,
  onSeedConsumed,
  onSnapshot,
  // Where in the manuscript this comment is being made: { blockId, chapterId,
  // lineHint }. Captured from the live selection by the editor (see
  // anchorFromRange) and sent on create so the Collab index can file the
  // comment under the right chapter and line. Null for comments started from
  // the header's "+ New", which aren't tied to a passage.
  anchor = null,
  onAnchorConsumed,
  // Who can be picked in the Notify dialog, and who "you" are (drives the
  // owner-only Edit action). Both come from the editor, which already knows
  // the draft's roster.
  collaborators = [],
  currentUserId,
  // Gate the owner-only card actions. Defaulting both to false hides actions
  // rather than exposing them, and the server refuses them regardless.
  isAdmin = false,
  isItemOwner = false,
}) {
  const [showAll, setShowAll] = useState(defaultShowAll);
  const [internalComposerOpen, setInternalComposerOpen] = useState(false);
  const composerOpen = showComposer !== undefined ? showComposer : internalComposerOpen;
  const setComposerOpen = typeof onToggleComposer === 'function' ? onToggleComposer : setInternalComposerOpen;
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  // Bumped by card actions (discard/edit/tag/notify) to force an immediate
  // refetch instead of waiting out the 12s poll.
  const [reloadKey, setReloadKey] = useState(0);

  // Close on ESC when open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Load + poll comments when panel is open
  useEffect(() => {
    if (!open || !bookId) return;
    let alive = true;
    let firstRun = true; // only show spinner on the very first load

    const load = async () => {
      try {
        if (firstRun) {
          setLoading(true);
        }
        setErr('');
        const data = await commentApi.list({
          bookId,
          type: itemType,
          page: showAll ? null : currentPage, // server filters when page provided
          limit: 50,
        });
        if (alive) {
          setComments(data);
        }
      } catch (e) {
        if (alive) {
          setErr(
            typeof e === 'string' ? e : (e?.message || 'Failed to load comments'),
          );
        }
      } finally {
        if (alive && firstRun) {
          setLoading(false);
          firstRun = false; // next polls won’t toggle the spinner
        }
      }
    };

    // initial load (with spinner)
    load();

    // later polls (no spinner; comments just appear)
    const t = setInterval(load, 12000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [open, bookId, itemType, currentPage, showAll, reloadKey]);
  useEffect(() => {
  if (typeof onSnapshot === 'function') onSnapshot(comments || []);
}, [comments, onSnapshot]);

  
  // Composer handler now receives { quote, highlight }
  const handleSubmit = async (text, payload = {}) => {
  if (!bookId || !text?.trim()) return;
  try {
    setErr('');
    const created = await commentApi.create({
      bookId,
      type: itemType,
      text,
      page: showAll ? null : currentPage,
      highlight: payload.highlight || null,
      quote: payload.quote || null,     // ⬅️ send the quote too
      blockId: anchor?.blockId || null,
      chapterId: anchor?.chapterId || null,
      lineHint: anchor?.lineHint ?? null,
    });
    setComments((prev) => [created, ...prev]);
    // Released only on success, so a failed post can be retried against the
    // same passage rather than silently becoming an unanchored comment.
    onAnchorConsumed?.();
  } catch (e) {
    setErr(e?.message || 'Failed to post comment.');
  }
};


  return (
    <div className={`comment-sidebar ${open ? 'open' : ''}`} aria-hidden={!open}>
      <div className="sidebar-header">
        {/* Row 1: title + close */}
        <div className="sidebar-header-row">
          <h3>Comments</h3>
          <IconButton
            aria-label="Close comments"
            onClick={onClose}
            className="sidebar-close-btn"
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        {/* Row 2: actions */}
        <div className="sidebar-actions">
          <button
            className="new-comment-btn"
            type="button"
            onClick={() => { setComposerOpen(true); onComposeOpen?.(); }}
            title="Add a new comment"
          >
            + New
          </button>

          {!hideFilter && (
            <button
              className="see-all-btn"
              type="button"
              onClick={() => setShowAll((s) => !s)}
              title={showAll ? 'Show comments on current page only' : 'Show all comments'}
            >
              {showAll ? 'This page' : 'All pages'}
            </button>
          )}
        </div>
      </div>

      {loading && <div className="sidebar-loading">Loading…</div>}
      {err && <div className="sidebar-error">{err}</div>}

      {/* Composer pinned at the top so it's visible the moment it opens —
          whether triggered from the header "+ New" button or a selection chip.
          The header owns the trigger, so the composer hides its own when closed. */}
      <CommentComposer
        onSubmit={handleSubmit}
        onBeginQuotePicking={onBeginQuotePicking}
        onPickQuote={onPickQuote}
        onEndQuotePicking={onEndQuotePicking}
        onRegisterQuoteAppend={onRegisterQuoteAppend}
        onComposeOpen={onComposeOpen}
        onComposeClose={onComposeClose}
        hideTrigger
        seedQuote={seedQuote}
        onSeedConsumed={onSeedConsumed}

        /* NEW: parent controls open/close */
        showForm={composerOpen}
        setShowForm={setComposerOpen}
      />

      <CommentList
        comments={comments}
        bookId={bookId}
        itemType={itemType}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        isItemOwner={isItemOwner}
        collaborators={collaborators}
        onChanged={() => setReloadKey((k) => k + 1)}
      />
    </div>
  );
}
